import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { BellDot, CheckCircle, Edit2, EllipsisVertical, FileIcon, Lock, Trash2, XCircle } from "lucide-react";
import { useState } from "react";

import { isUpclaim } from "@upclaim/sdk/src/admin-roles";

import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

import { useDeleteClaimNote, useUpdateClaimNote } from "../../api/use-claim-mutations";
import { ClaimNoteEditor } from "./claim-note-editor";
import { deriveTaskStatus, splitMentionSegments, type TaskStatus, type TimelineNote } from "./timeline-utils";

const taskStatusColorClasses: Record<TaskStatus, string> = {
  completed: "text-green-700 bg-green-50 border-green-200",
  rejected: "text-red-700 bg-red-50 border-red-200",
  overdue: "text-pink-700 bg-pink-50 border-pink-200",
  pending: "text-indigo-700 bg-indigo-50 border-indigo-200",
};

/** Renders note content with @mentions highlighted as blue pills (no dangerouslySetInnerHTML). */
function HighlightedContent({ content }: { content: string }) {
  return (
    <>
      {splitMentionSegments(content).map((segment, index) =>
        segment.type === "mention" ? (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: segments have no stable identity
            key={index}
            className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset"
          >
            @{segment.value}
          </span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: segments have no stable identity
          <span key={index}>{segment.value}</span>
        ),
      )}
    </>
  );
}

interface ClaimNoteElementProps {
  claimId: string;
  note: TimelineNote;
}

/** Port of timeline/ClaimNoteElement.vue. */
export function ClaimNoteElement({ claimId, note }: ClaimNoteElementProps) {
  const { user, roles } = useCurrentUser();

  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const updateNote = useUpdateClaimNote(claimId);
  const deleteNote = useDeleteClaimNote(claimId);

  const noteDate = format(new Date(note.createdAt), "dd MMM", { locale: fr });

  // Behavior parity with the Vue app: Upclaim members can edit/delete all notes, plus the creator.
  const isNoteCreator = note.admin?.auth0Id === user?.sub;
  const canEdit = roles.some(isUpclaim) || isNoteCreator;

  const isTogglingVisibility = updateNote.isPending;
  const visibilityTooltip =
    note.visibility === "PRIVATE" ? "Cliquer pour rendre publique." : "Cliquer pour rendre privée.";

  const task = note.claimTask;
  const taskStatus = task ? deriveTaskStatus(task) : null;
  const taskDueDate = task?.dueDate ? format(new Date(task.dueDate), "dd/MM/yyyy", { locale: fr }) : "";
  const taskStatusLabel =
    taskStatus === "completed"
      ? "Complétée"
      : taskStatus === "rejected"
        ? "Rejetée"
        : taskStatus === "overdue"
          ? `Rappel expiré ${taskDueDate}`
          : taskStatus === "pending"
            ? `Rappel à venir ${taskDueDate}`
            : "";

  function toggleVisibility() {
    if (isTogglingVisibility) {
      return;
    }
    const newVisibility = note.visibility === "PRIVATE" ? "PUBLIC" : "PRIVATE";
    updateNote.mutate({
      noteId: note.id,
      payload: { visibility: newVisibility },
      successMessage: newVisibility === "PUBLIC" ? "Note rendue publique" : "Note rendue privée",
    });
  }

  function previewAttachment() {
    if (note.attachment?.file) {
      setPreviewFile(note.attachment.file);
      setIsPreviewOpen(true);
    }
  }

  if (isEditing) {
    return (
      <li className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="flex-1">
          <ClaimNoteEditor
            claimId={claimId}
            noteId={note.id}
            initialContent={note.content}
            initialFile={note.attachment?.file ?? null}
            editMode
            onCancel={() => setIsEditing(false)}
            onSaved={() => setIsEditing(false)}
          />
        </div>
      </li>
    );
  }

  return (
    <li className="group flex items-start justify-between gap-4 px-4 py-3 transition-colors hover:bg-gray-50">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {note.admin.profilePictureUrl ? (
          <img
            src={note.admin.profilePictureUrl}
            alt={note.admin.fullName}
            className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
            <span className="text-xs font-medium text-gray-600">{note.admin.fullName?.charAt(0)}</span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <p className="flex-1 whitespace-pre-line text-sm text-gray-700">
              <span className="font-medium text-gray-900">
                <HighlightedContent content={note.content} />
                <span className="mx-1 text-gray-400">|</span>
              </span>
              {note.admin.fullName}
            </p>

            {note.visibility === "PRIVATE" ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      onClick={toggleVisibility}
                      disabled={isTogglingVisibility}
                      className={cn(
                        "flex-shrink-0 rounded p-1 text-zinc-500 transition-colors hover:bg-gray-100",
                        isTogglingVisibility ? "cursor-wait opacity-50" : "cursor-pointer",
                      )}
                    />
                  }
                >
                  <Lock className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>{visibilityTooltip}</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      onClick={toggleVisibility}
                      disabled={isTogglingVisibility}
                      className={cn(
                        "flex-shrink-0",
                        isTogglingVisibility ? "cursor-wait opacity-50" : "cursor-pointer",
                      )}
                    />
                  }
                >
                  <span className="inline-flex items-center rounded-full bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-purple-700/10 ring-inset">
                    Publique
                  </span>
                </TooltipTrigger>
                <TooltipContent>{visibilityTooltip}</TooltipContent>
              </Tooltip>
            )}
          </div>

          {note.attachment?.file ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    onClick={previewAttachment}
                    className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs transition-colors hover:bg-gray-100"
                  />
                }
              >
                <FileIcon className="h-3 w-3 text-gray-500" />
                <span className="text-gray-700">{note.attachment.file.name}</span>
              </TooltipTrigger>
              <TooltipContent>Prévisualiser le fichier</TooltipContent>
            </Tooltip>
          ) : null}

          {task && taskStatus ? (
            <div
              className={cn(
                "mt-2 inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs",
                taskStatusColorClasses[taskStatus],
              )}
            >
              {taskStatus === "completed" ? (
                <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
              ) : taskStatus === "rejected" ? (
                <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
              ) : (
                <BellDot className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="font-medium">{taskStatusLabel}</span>
              {task.assignedAdmin ? (
                <span className="ml-1 flex items-center gap-1">
                  <span className="text-gray-500">→</span>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        task.assignedAdmin.profilePictureUrl ? (
                          <img
                            src={task.assignedAdmin.profilePictureUrl}
                            alt={task.assignedAdmin.fullName}
                            className="h-4 w-4 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex h-4 w-4 items-center justify-center rounded-full bg-gray-200">
                            <span className="text-[10px] font-medium text-gray-600">
                              {task.assignedAdmin.fullName?.charAt(0)}
                            </span>
                          </div>
                        )
                      }
                    />
                    <TooltipContent>{task.assignedAdmin.fullName}</TooltipContent>
                  </Tooltip>
                </span>
              ) : null}
            </div>
          ) : null}

          {String(note.updatedAt) !== String(note.createdAt) ? (
            <p className="mt-1 text-xs text-gray-400">(modifiée)</p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <span className="whitespace-nowrap text-xs text-gray-400">{noteDate}</span>

        {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <button
                  type="button"
                  className="flex items-center rounded-full p-1 text-gray-400 hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                />
              }
            >
              <span className="sr-only">Options</span>
              <EllipsisVertical className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isTogglingVisibility} onClick={toggleVisibility}>
                {note.visibility === "PRIVATE" ? (
                  <Lock className="h-4 w-4 text-zinc-500" />
                ) : (
                  <span className="inline-flex items-center rounded-full bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-purple-700/10 ring-inset">
                    Public
                  </span>
                )}
                <span className={note.visibility === "PUBLIC" ? "ml-1" : ""}>
                  {note.visibility === "PRIVATE" ? "Rendre publique" : "Rendre privée"}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={() => setIsDeleteConfirmOpen(true)}>
                <Trash2 className="h-4 w-4" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la note</AlertDialogTitle>
            <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer cette note ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Annuler</AlertDialogClose>
            <AlertDialogClose
              render={<Button variant="destructive" />}
              onClick={() => {
                deleteNote.mutate(note.id);
                setIsDeleteConfirmOpen(false);
              }}
            >
              Supprimer
            </AlertDialogClose>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FilePreviewDialog
        open={isPreviewOpen}
        file={previewFile}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewFile(null);
        }}
      />
    </li>
  );
}
